import { useCallback, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { ArrowDownWideNarrow } from 'lucide-react';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import { buildClassName } from 'utils/build-class-name';
import { getLocalizedSortOptions } from 'utils/localized-select-options';
import { AVAILABLE_STATUS } from 'contexts/preferences/listing-params/types';
import type { Sort, Status } from 'contexts/preferences/listing-params/types';
import { searchFilterIds } from 'constants/test-constants';
import { Namespaces } from 'types/locales';
import { ListingsViewToggle } from './listings-view-toggle';
import styles from './listings-controls.module.scss';

export type ListingsControlsProps = {
  sort: Sort;
  status?: Status;
  onSortChanged?: (sort: Sort) => void;
  isFullScreen: boolean;
  onSetFullScreen?: (fullScreen: boolean) => void;
  isMapFullscreen: boolean;
  onSetMapFullscreen?: (mapFullscreen: boolean) => void;
  /** Hides the map/split/grid view toggle when there is no map to switch to (province/state/country pages). */
  hideViewToggle?: boolean;
};

export function ListingsControls({
  sort,
  status,
  onSortChanged,
  isFullScreen,
  onSetFullScreen,
  isMapFullscreen,
  onSetMapFullscreen,
  hideViewToggle = false,
}: ListingsControlsProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const [isSortOpen, setIsSortOpen] = useState(false);

  const closeSort = useCallback(() => setIsSortOpen(false), []);
  const sortWrapRef = useOutsideClickHandler<HTMLDivElement>(isSortOpen ? closeSort : undefined);

  const sortingOptions = getLocalizedSortOptions(status || AVAILABLE_STATUS, t);

  const handleSelectSort = (value: Sort) => {
    onSortChanged?.(value);
    setIsSortOpen(false);
  };

  return (
    <div className={styles.controls}>
      <div className={styles.sortWrap} ref={sortWrapRef}>
        <button
          type="button"
          className={styles.sortButton}
          data-testid={searchFilterIds.sortByDropdown}
          aria-haspopup="menu"
          aria-expanded={isSortOpen}
          onClick={() => setIsSortOpen(prev => !prev)}
        >
          <ArrowDownWideNarrow className={styles.sortIcon} size={15} strokeWidth={2} aria-hidden />
          {t('area_page_v2.listings.sort_label', { defaultValue: 'Sort' })}
        </button>
        {isSortOpen && (
          <div className={styles.sortMenu} role="menu" data-testid="AreaPageV2-SortMenu">
            {sortingOptions.map(option => (
              <button
                key={option.value}
                type="button"
                role="menuitemradio"
                aria-checked={option.value === sort}
                className={buildClassName(styles.sortOption, option.value === sort && styles.sortOptionActive)}
                onClick={() => handleSelectSort(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {!hideViewToggle && (
        <ListingsViewToggle
          isFullScreen={isFullScreen}
          onSetFullScreen={onSetFullScreen}
          isMapFullscreen={isMapFullscreen}
          onSetMapFullscreen={onSetMapFullscreen}
        />
      )}
    </div>
  );
}
